import { useCallback, useMemo, useState } from "react";
import {
  Badge,
  Center,
  Group,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconMoodEmpty } from "@tabler/icons-react";
import { Link } from "react-router-dom";

import {
  ReportDateRangePicker,
  getLastMonthRange,
  type DateRange,
} from "../../components/reports/ReportDateRangePicker";
import { ReportExportButton } from "../../components/reports/ReportExportButton";
import { useCancerCaseReport } from "../../hooks/useReports";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import { formatLabShortDate } from "../../utils/labTime";
import { reportsService } from "../../services/reports.service";
import { canonicalReportDateRangeParams } from "./reportDateRangeParams";

const DEFAULT_RANGE: DateRange = getLastMonthRange();

const URGENCY_COLORS: Record<string, string> = {
  routine: "gray",
  urgent: "orange",
  critical: "red",
};

export function CancerCaseReportContent() {
  const labToday = useLabTodayDateOnly();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => canonicalReportDateRangeParams(dateRange),
    [dateRange]
  );
  const { data, isLoading } = useCancerCaseReport(params, page);
  const totalPages = useMemo(
    () => (data ? Math.ceil(data.pagination.total / data.pagination.limit) : 1),
    [data]
  );

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
  }, []);
  const handleExport = useCallback(
    () => reportsService.exportCancerCases(params),
    [params]
  );

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group align="flex-end" wrap="wrap" gap="md">
            <ReportDateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
            />
          </Group>
          <ReportExportButton
            filename={`cancer-cases-${labToday}.csv`}
            fetchData={handleExport}
          />
        </Group>
      </Paper>

      {data && (
        <Paper p="md" withBorder>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Total Cancer Cases
          </Text>
          <Text size="xl" fw={700} c={data.totalCount > 0 ? "red" : "dimmed"}>
            {data.totalCount}
          </Text>
          <Text size="xs" c="dimmed">
            in selected period
          </Text>
        </Paper>
      )}

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[...Array(5)].map((_, index) => (
              <Skeleton key={index} h={36} />
            ))}
          </Stack>
        ) : data?.data.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <IconMoodEmpty
                size={48}
                stroke={1.2}
                color="var(--mantine-color-gray-6)"
              />
              <Text c="dimmed">
                No cancer cases found for the selected period.
              </Text>
            </Stack>
          </Center>
        ) : (
          <>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Case #</Table.Th>
                  <Table.Th>Patient</Table.Th>
                  <Table.Th>Malignancy Type</Table.Th>
                  <Table.Th>Urgency</Table.Th>
                  <Table.Th>Identified</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.data.map((row) => (
                  <Table.Tr key={row.caseId}>
                    <Table.Td>
                      <Text
                        component={Link}
                        to={`/cases/${row.caseId}`}
                        c="blue"
                        size="sm"
                      >
                        {row.caseNumber}
                      </Text>
                    </Table.Td>
                    <Table.Td>{row.patientName}</Table.Td>
                    <Table.Td>{row.malignancyType ?? "—"}</Table.Td>
                    <Table.Td>
                      <Badge
                        color={URGENCY_COLORS[row.urgencyLevel] ?? "gray"}
                        variant="light"
                      >
                        {row.urgencyLevel}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {formatLabShortDate(row.cancerIdentifiedAt)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {totalPages > 1 && (
              <Group justify="center" p="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                />
              </Group>
            )}
          </>
        )}
      </Paper>
    </Stack>
  );
}
